<template>
  <RevCard class="my-32 flex flex-col gap-32 p-16">
    <!-- Legal name -->
    <section
      v-if="displayedData.withLegalName && sellerInfo?.merchant_legal_name"
      class="flex flex-col gap-4"
    >
      <p class="body-1-bold">
        {{ i18n(translations.aboutTabLegalName) }}
      </p>
      <p class="body-1">
        {{ sellerInfo?.merchant_legal_name }}
      </p>
    </section>

    <!-- VAT number -->
    <section
      v-if="displayedData.withTaxNumber && sellerInfo?.vat_number"
      class="flex flex-col gap-4"
    >
      <p class="body-1-bold">
        {{ i18n(translations.aboutTabVatNumber) }}
      </p>
      <p class="body-1">
        {{ sellerInfo?.vat_number }}
      </p>
    </section>

    <!-- SIREN number -->
    <section
      v-if="displayedData.withCompanyNumber && companyNumber"
      class="flex flex-col gap-4"
    >
      <p class="body-1-bold">
        {{ i18n(translations.aboutTabSiretNumber) }}
      </p>
      <p class="body-1">
        {{ companyNumber }}
      </p>
    </section>

    <!-- Legal address -->
    <section
      v-if="displayedData.withLegalAddress && legalAddresslines"
      class="flex flex-col gap-4"
    >
      <p class="body-1-bold">
        {{ i18n(translations.aboutTabAddress) }}
      </p>
      <p class="body-1">
        <span
          v-for="addressLine in legalAddresslines"
          :key="addressLine"
          class="block"
        >
          {{ addressLine }}
        </span>
      </p>
    </section>

    <!-- Country of origin -->
    <section
      v-if="displayedData.withCountry && countryCode"
      class="flex flex-col gap-4"
    >
      <p class="body-1-bold">
        {{ i18n(translations.aboutTabCountryOfOrigin) }}
      </p>
      <p class="body-1">
        {{ country }}
        <Flag
          v-if="countryCode"
          aria-hidden="true"
          class="ml-2 inline-block align-baseline"
          :country-code
        />
      </p>
    </section>

    <!-- Japan only -->
    <RevInfoBlock
      v-if="displayedData.japanData"
      :content="i18n(translations.konbiniBlockContent)"
      :icon="IconInfo"
      :title="i18n(translations.konbiniBlockTitle)"
      variant="info"
    />

    <section v-for="data in displayedData.japanData" :key="data.value">
      <span class="body-1-bold mr-2">{{ data.label }}</span>
      <span class="body-1">{{ data.value }}</span>
    </section>
  </RevCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { CountryCodeValue } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useFormatAddress } from '@backmarket/nuxt-module-address/useFormatAddress'
import { useInlineFormatAddress } from '@backmarket/nuxt-module-address/useInlineFormatAddress'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconInfo } from '@ds/icons/IconInfo'

import type { SellerProductListPage } from '~/api/seller/types/SellerProductListPage'

import translations from './AboutTab.translations'

const i18n = useI18n()
const { sellerInfo, countryCode, country } = defineProps<{
  country?: string
  countryCode?: CountryCodeValue
  sellerInfo?: SellerProductListPage
}>()

const legalAddresslines = sellerInfo?.address
  ? useFormatAddress(
      { ...sellerInfo?.address, postalCode: sellerInfo?.address?.postal_code },
      {
        includeCompany: true,
        includeCountry: true,
      },
    )
  : undefined

const inlineLegalAddress = sellerInfo?.address
  ? useInlineFormatAddress(
      { ...sellerInfo?.address, postalCode: sellerInfo?.address?.postal_code },
      {
        includeCompany: false,
      },
    )
  : undefined

const companyNumber = computed(() =>
  countryCode === 'FR'
    ? sellerInfo?.siret_number.replace(/\s/g, '').substring(0, 9)
    : sellerInfo?.siret_number,
)

const displayedData = computed(() => {
  switch (countryCode) {
    case 'US':
      return {
        withLegalAddress: true,
        withCountry: true,
      }
    case 'AU':
      return {
        withLegalName: true,
        withTaxNumber: true,
      }
    case 'JP':
      return {
        japanData: [
          {
            label: i18n(translations.konbiniCompanyName),
            value: sellerInfo?.merchant_legal_name,
          },
          {
            label: i18n(translations.konbiniStoreName),
            value: sellerInfo?.company,
          },
          {
            label: i18n(translations.landingSellerDescriptionAddress),
            value: inlineLegalAddress,
          },
          {
            label: i18n(translations.konbiniEmail),
            value: sellerInfo?.custom_data?.japan?.konbini_email,
          },
          {
            label: i18n(translations.konbiniPhoneNumber),
            value: sellerInfo?.custom_data?.japan?.konbini_phone_number,
          },
          {
            label: i18n(translations.konbiniSecondHandDealerNumber),
            value: sellerInfo?.custom_data?.japan?.secondhand_dealer_number,
          },
          {
            label: i18n(
              translations.konbiniSecondhandDealerNumberIssuingPrefecture,
            ),
            value:
              sellerInfo?.custom_data?.japan
                ?.secondhand_dealer_number_issuing_prefecture,
          },
          {
            label: i18n(translations.konbiniStoreOperationManager),
            value: sellerInfo?.custom_data?.japan?.store_operation_manager,
          },
          {
            label: i18n(translations.konbiniStoreSecurityOfficer),
            value: sellerInfo?.custom_data?.japan?.store_security_officer,
          },
        ].filter((data) => data.value),
      }
    default:
      return {
        withTaxNumber: true,
        withCompanyNumber: true,
        withLegalAddress: true,
        withCountry: true,
      }
  }
})
</script>
